import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ButtonType } from '@xemplo/button';
import { DawnTriangleAlert } from '@xemplo/icons';
import { ModalBodyOverflow, ModalSize, ModalTheme } from '@xemplo/modal';
import { useSubmitAmendments } from '@xemplo/payrun-query';
import { PromptProps, PromptType } from '@xemplo/prompts';
import { ToastType, useToast } from '@xemplo/toast';

import * as S from './payrun-submit-amendments.styles';
export const useConfirmPrompt = () => {
  const [promptVisible, setPromptVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addToast } = useToast();
  const { id } = useParams();
  const { mutateAsync: submitAmendments } = useSubmitAmendments({
    payrunId: id ?? null,
    onSuccess: () => {
      setPromptVisible(false);
      addToast({
        text: 'Amendments submitted',
        type: ToastType.Confirmation,
      });
    },
    onError: (error) => {
      const errorMessage = error.message ?? 'Something went wrong, please try again.';
      setErrorMessage(errorMessage);
    },
  });

  //config for prompt modal
  const initialPromptProps = useMemo<PromptProps>(() => {
    return {
      open: false,
      type: PromptType.General,
      theme: ModalTheme.Light,
      size: ModalSize.Small,
      closeButton: { hide: true },
      header: {
        customHeader: (
          <S.PromptHeader>
            <DawnTriangleAlert />
            <S.PromptHeaderTitle>Are you sure you want to submit?</S.PromptHeaderTitle>
          </S.PromptHeader>
        ),
      },
      body: {
        content: (
          <S.PromptBody>
            You will not be able to edit or delete the amendments once your payroll
            manager has approved them.
          </S.PromptBody>
        ),
        overflow: ModalBodyOverflow.Extended,
      },
      footer: {
        btnPrimary: {
          label: 'Submit',
          ariaLabel: 'Submit Btn',
          onClickAsync: submitAmendments,
          type: ButtonType.Primary,
        },
        btnSecondary: {
          label: 'Cancel',
          ariaLabel: 'Cancel Btn',
          type: ButtonType.Secondary,
          onClick: () => setPromptVisible(false),
        },
      },
    };
  }, [submitAmendments]);

  const [promptProps, setPromptProps] = useState<PromptProps>(initialPromptProps);

  useEffect(() => {
    if (errorMessage) {
      setPromptProps({
        ...initialPromptProps,
        body: {
          content: (
            <>
              <S.PromptBody>
                Are you sure you want to remove amendment? You can't undo changes.
              </S.PromptBody>
              <S.ErrorMessage>
                <DawnTriangleAlert />
                {errorMessage}
              </S.ErrorMessage>
            </>
          ),
          overflow: ModalBodyOverflow.Extended,
        },
      });
    } else {
      setPromptProps(initialPromptProps);
    }
  }, [errorMessage, initialPromptProps]);

  useEffect(() => {
    if (initialPromptProps.open) {
      setPromptVisible(true);
    }
  }, [initialPromptProps.open]);

  return { promptProps, promptVisible, setPromptVisible };
};
